import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { ReactComponent as PuckSvg } from "../assets/puck.svg";
import { SIDE } from "_constants";

const VISIBLE_EVENT_CODES = new Set([
  "1270", // On Possession
  "1292", // On Possession (alt)
  "1295", // In Possession
  "1289", // Power Play
  "1271", // Penalty
  "1273", // Shot
  "1275", // Penalty Shot
  "1276", // Penalty Shot Missed
  "1284", // Face Off
  "1285", // Puck Dropped
]);

const getSideEnum = (side: any): SIDE | null => {
  if (side === SIDE.HOME || side === 1 || side === "home") return SIDE.HOME;
  if (side === SIDE.AWAY || side === 2 || side === "away") return SIDE.AWAY;
  return null;
};

const getDefaultCoords = (side: any) => {
  const resolved = getSideEnum(side);
  if (resolved === SIDE.HOME) return { x: 0.6, y: 0.5 };
  if (resolved === SIDE.AWAY) return { x: 0.4, y: 0.5 };
  return { x: 0.5, y: 0.5 };
};

const parseXY = (xy?: string | null) => {
  if (!xy) return undefined;
  const parts = xy.split(",");
  if (parts.length !== 2) return undefined;
  const [rawX, rawY] = parts.map((v) => Number(v.trim()));
  if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return undefined;
  return { x: rawX, y: rawY };
};

export const LivePuck: FC<{ status: any }> = ({ status }) => {
  const { xy, side, sc, nsc, x: numericX, y: numericY } = status || {};
  const [{ x, y, isActive }, setState] = useState({
    x: 0,
    y: 0,
    isActive: false,
  });

  const normalizedCode = useMemo(() => {
    if (typeof nsc === "string" && nsc.length) return nsc;
    if (typeof nsc === "number") return String(nsc).slice(-4);
    if (typeof sc === "string" && sc.length >= 4) return sc.slice(-4);
    if (typeof sc === "number") return String(sc).slice(-4);
    return "";
  }, [nsc, sc]);

  const shouldShow = VISIBLE_EVENT_CODES.has(normalizedCode);

  useEffect(() => {
    if (!shouldShow) {
      setState((s) => ({ ...s, isActive: false }));
      return;
    }

    const defaultCoords = getDefaultCoords(side);

    const coordsFromXY = parseXY(xy);
    if (coordsFromXY) {
      setState({ ...coordsFromXY, isActive: true });
      return;
    }

    if (
      typeof numericX === "number" &&
      Number.isFinite(numericX) &&
      typeof numericY === "number" &&
      Number.isFinite(numericY)
    ) {
      setState({ x: numericX, y: numericY, isActive: true });
      return;
    }

    setState({ ...defaultCoords, isActive: true });
  }, [xy, side, shouldShow, numericX, numericY]);

  const puckStyle: CSSProperties = useMemo(
    () => ({ left: `${x * 100}%`, top: `${y * 100}%` }),
    [x, y]
  );

  if (!isActive) return <></>;

  return (
    <PuckSvg
      className="absolute z-[1] h-[3vw] w-[4vw] transition-[left,top] duration-[2000ms] ease-[cubic-bezier(0.55,0,0.11,1)]"
      style={puckStyle}
    />
  );
};
